import Phaser from 'phaser';
import type { Player } from './Player';

export interface PetFollowerOpts {
  scene: Phaser.Scene;
  owner: Player;
  name: string;
  tier: number;
  petCode?: string;
}

const TIER_COLORS: Record<number, number> = {
  1: 0x27ae60,
  2: 0x2980b9,
  3: 0x8e44ad,
  4: 0xe67e22,
  5: 0xe74c3c,
};

const ORBIT_RADIUS = 28;
const ORBIT_SPEED = 1.8; // radians per second
const BOB_AMP = 3;       // vertical float amplitude
const BOB_SPEED = 3;     // vertical float speed

export class PetFollower extends Phaser.GameObjects.Container {
  private owner: Player;
  private orbitAngle = 0;
  private gfx!: Phaser.GameObjects.Graphics;
  private sprite?: Phaser.GameObjects.Sprite;
  private label!: Phaser.GameObjects.Text;

  constructor(opts: PetFollowerOpts) {
    super(opts.scene, opts.owner.x, opts.owner.y);
    this.owner = opts.owner;

    const color = TIER_COLORS[opts.tier] ?? 0x9b59b6;
    const code = opts.petCode?.replace(/^pet\./, '') ?? null;
    const texKey = code ? `pet_${code}` : null;
    const hasSprite = texKey && opts.scene.textures.exists(texKey);

    if (hasSprite) {
      // Use generated sprite sheet
      this.sprite = opts.scene.add.sprite(0, 0, texKey);
      this.sprite.setDisplaySize(48, 48);
      const animKey = `${texKey}_idle`;
      if (opts.scene.anims.exists(animKey)) {
        this.sprite.play(animKey);
      }
      this.add(this.sprite);
    } else {
      // Fallback: procedural graphics
      const glow = opts.scene.add.graphics();
      glow.fillStyle(color, 0.2);
      glow.fillCircle(0, 0, 10);

      this.gfx = opts.scene.add.graphics();
      this.gfx.fillStyle(color, 1);
      this.gfx.fillCircle(0, 0, 6);
      this.gfx.lineStyle(1, 0xffffff, 0.6);
      this.gfx.strokeCircle(0, 0, 6);

      this.add([glow, this.gfx]);
    }

    this.label = opts.scene.add.text(0, hasSprite ? -30 : -14, opts.name, {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '9px', color: '#ccc',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5);

    this.add(this.label);
    opts.scene.add.existing(this);
    this.setDepth(11);
  }

  update(_time: number, delta: number) {
    const dt = delta / 1000;
    this.orbitAngle += ORBIT_SPEED * dt;

    const ox = Math.cos(this.orbitAngle) * ORBIT_RADIUS;
    const oy = Math.sin(this.orbitAngle) * ORBIT_RADIUS * 0.5; // elliptical orbit
    const bob = Math.sin(this.orbitAngle * BOB_SPEED) * BOB_AMP;

    this.x = this.owner.x + ox;
    this.y = this.owner.y + oy + bob;
    this.setDepth(this.owner.depth + (oy > 0 ? 1 : -1));
  }

  destroy(fromScene?: boolean) {
    this.gfx?.destroy();
    this.sprite?.destroy();
    this.label?.destroy();
    super.destroy(fromScene);
  }
}
